import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Thermometer, Wind, Droplets, AlertCircle, Save, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { API_BASE_URL } from '@/config/api';

interface AlertThreshold {
  id: string;
  location_name: string;
  latitude: number;
  longitude: number;
  high_temp_alert?: number;
  low_temp_alert?: number;
  high_wind_alert?: number;
  rain_alert?: number;
  high_uv_alert?: number;
  high_humidity_alert?: number;
  low_humidity_alert?: number;
}

export function AlertSettingsCard() {
  const { t } = useTranslation();
  const [thresholds, setThresholds] = useState<AlertThreshold[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { token } = useAuthStore();

  const [formData, setFormData] = useState<Partial<AlertThreshold>>({
    location_name: '',
    latitude: 48.1351, // München default
    longitude: 11.5820,
  });

  useEffect(() => {
    loadThresholds();
  }, []);

  const loadThresholds = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/alerts/thresholds`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setThresholds(data.thresholds || []);
      }
    } catch (error) {
      console.error('Failed to load thresholds:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveThreshold = async () => {
    try {
      const url = editingId 
        ? `${API_BASE_URL}/alerts/thresholds/${editingId}`
        : `${API_BASE_URL}/alerts/thresholds`;
      
      const method = editingId ? 'PUT' : 'POST';

      console.log('Sending threshold:', formData);

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Backend error:', error);
        alert(`Fehler: ${error.error || 'Unbekannter Fehler'}`);
        return;
      }

      await loadThresholds();
      setShowForm(false);
      setEditingId(null);
      setFormData({ location_name: '', latitude: 48.1351, longitude: 11.5820 });
    } catch (error) {
      console.error('Failed to save threshold:', error);
    }
  };

  const deleteThreshold = async (id: string) => {
    if (!confirm('Möchten Sie diesen Schwellenwert wirklich löschen?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/alerts/thresholds/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await loadThresholds();
      }
    } catch (error) {
      console.error('Failed to delete threshold:', error);
    }
  };

  const editThreshold = (threshold: AlertThreshold) => {
    setEditingId(threshold.id);
    setFormData(threshold);
    setShowForm(true);
  };

  const cancelEdit = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ location_name: '', latitude: 48.1351, longitude: 11.5820 });
  };

  return (
    <div className="bg-surface rounded-xl p-6 border border-border shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-bold text-text-primary">
            {t('alerts.settings')}
          </h3>
        </div>
        <button
          onClick={showForm ? cancelEdit : () => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? t('alerts.cancel') : `${t('alerts.new')} ${t('alerts.createThreshold')}`}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-6 p-4 bg-surface-elevated rounded-lg border border-border"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                {t('alerts.location')}
              </label>
              <input
                type="text"
                value={formData.location_name || ''}
                onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                placeholder="z.B. München"
                className="w-full px-4 py-2 bg-surface border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-text-primary"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Thermometer className="w-4 h-4 inline mr-1" />
                  {t('alerts.highTempLabel')}
                </label>
                <input
                  type="number"
                  value={formData.high_temp_alert || ''}
                  onChange={(e) => setFormData({ ...formData, high_temp_alert: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="z.B. 35"
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Thermometer className="w-4 h-4 inline mr-1" />
                  {t('alerts.lowTempLabel')}
                </label>
                <input
                  type="number"
                  value={formData.low_temp_alert || ''}
                  onChange={(e) => setFormData({ ...formData, low_temp_alert: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="z.B. -10"
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Wind className="w-4 h-4 inline mr-1" />
                  {t('alerts.windLabel')}
                </label>
                <input
                  type="number"
                  value={formData.high_wind_alert || ''}
                  onChange={(e) => setFormData({ ...formData, high_wind_alert: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="z.B. 60"
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Droplets className="w-4 h-4 inline mr-1" />
                  {t('alerts.rainLabel')}
                </label>
                <input
                  type="number"
                  value={formData.rain_alert || ''}
                  onChange={(e) => setFormData({ ...formData, rain_alert: e.target.value ? Number(e.target.value) : undefined })}
                  placeholder="z.B. 80"
                  className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary"
                />
              </div>
            </div>

            <button
              onClick={saveThreshold}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
              {editingId ? t('alerts.update') : t('alerts.save')}
            </button>
          </div>
        </motion.div>
      )}

      {/* Thresholds List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent" />
        </div>
      ) : thresholds.length === 0 ? (
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-text-secondary opacity-50 mx-auto mb-4" />
          <p className="text-text-primary text-lg font-medium">
            {t('ui.noThresholds')}
          </p>
          <p className="text-sm text-text-secondary mt-2">
            {t('ui.clickToCreateThreshold')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {thresholds.map((threshold) => (
            <motion.div
              key={threshold.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 border border-border rounded-lg hover:bg-surface-elevated transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-text-primary">{threshold.location_name}</h4>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => editThreshold(threshold)}
                    className="px-3 py-1 text-sm alert-edit-button rounded hover:alert-edit-button transition-colors"
                  >
                    {t('alerts.edit')}
                  </button>
                  <button
                    onClick={() => deleteThreshold(threshold.id)}
                    className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Active Thresholds */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                {threshold.high_temp_alert && (
                  <div className="p-2 badge-orange rounded flex items-center gap-1">
                    <Thermometer className="w-3 h-3 badge-orange-icon" />
                    <span>
                      T ≥ {threshold.high_temp_alert}°C
                    </span>
                  </div>
                )}
                {threshold.low_temp_alert && (
                  <div className="p-2 badge-blue rounded flex items-center gap-1">
                    <Thermometer className="w-3 h-3 badge-blue-icon" />
                    <span>
                      T ≤ {threshold.low_temp_alert}°C
                    </span>
                  </div>
                )}
                {threshold.high_wind_alert && (
                  <div className="p-2 badge-teal rounded flex items-center gap-1">
                    <Wind className="w-3 h-3 badge-teal-icon" />
                    <span>
                      Wind ≥ {threshold.high_wind_alert} km/h
                    </span>
                  </div>
                )}
                {threshold.rain_alert && (
                  <div className="p-2 badge-indigo rounded flex items-center gap-1">
                    <Droplets className="w-3 h-3 badge-indigo-icon" />
                    <span>
                      Regen ≥ {threshold.rain_alert}%
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
